// Vue Router 配置
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { title: '登录', requiresAuth: false },
  },
  {
    path: '/',
    redirect: '/home',
  },

  // ========== 首页 ==========
  {
    path: '/home',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: { title: '首页', requiresAuth: true },
  },

  // ========== 加工管理 ==========
  {
    path: '/processing',
    name: 'Processing',
    component: () => import('@/views/processing/ProcessingLayout.vue'),
    meta: { title: '加工管理', requiresAuth: true, permission: 'processing:task:view' },
    redirect: '/processing/task-center',
    children: [
      {
        path: 'task-center',
        name: 'ProcessingTaskCenter',
        component: () => import('@/views/processing/TaskCenter.vue'),
        meta: { title: '任务总览', requiresAuth: true },
      },
      {
        path: 'task-board',
        redirect: '/processing/task-center',
      },
      {
        path: 'task-list',
        redirect: '/processing/task-center',
      },
      {
        path: 'mold-initial-list',
        name: 'ProcessingMoldInitialList',
        component: () => import('@/views/processing/MoldInitialList.vue'),
        meta: { title: '模具初始列表', requiresAuth: true },
      },
      {
        path: 'application',
        name: 'ProcessingApplication',
        component: () => import('@/views/processing/Application.vue'),
        meta: { title: '加工申请', requiresAuth: true },
      },
      {
        // 2026-10-18 补齐：其他工件(非模 general)领料页，对齐小程序 non-mold-pickup（免审批领走）
        path: 'non-mold-pickup',
        name: 'ProcessingNonMoldPickup',
        component: () => import('@/views/processing/NonMoldPickup.vue'),
        meta: { title: '其他工件领料', requiresAuth: true },
      },
      {
        // 2026-10-18 补齐：主管排工序页，对齐小程序 non-mold-plan（按申请ID反查路线记录）
        path: 'non-mold-plan',
        name: 'ProcessingNonMoldPlan',
        component: () => import('@/views/processing/NonMoldPlan.vue'),
        meta: { title: '排工序', requiresAuth: true },
      },
      {
        // 2026-10-21 第三阶段：主管确认工分页，对齐小程序 non-mold-manage 待确认工分tab
        path: 'non-mold-confirm',
        name: 'ProcessingNonMoldConfirm',
        component: () => import('@/views/processing/NonMoldConfirm.vue'),
        meta: { title: '确认工分', requiresAuth: true },
      },
      {
        path: 'preset',
        name: 'ProcessingPreset',
        component: () => import('@/views/processing/Preset.vue'),
        meta: { title: '工序预设', requiresAuth: true },
      },
      {
        path: 'heat-treatment-batch',
        name: 'ProcessingHeatTreatmentBatch',
        component: () => import('@/views/processing/HeatTreatmentSendBatch.vue'),
        meta: { title: '委外热处理外发', requiresAuth: true },
      },
      {
        path: 'vendor',
        name: 'ProcessingVendor',
        component: () => import('@/views/processing/VendorManagement.vue'),
        meta: { title: '供应商管理', requiresAuth: true },
      },
      {
        path: 'gantt-chart',
        name: 'ProcessingGanttChart',
        component: () => import('@/views/processing/GanttChartView.vue'),
        meta: { title: '排程甘特图', requiresAuth: true },
      },
      {
        path: 'schedule-history',
        name: 'ProcessingScheduleHistory',
        component: () => import('@/views/processing/ScheduleHistory.vue'),
        meta: { title: '调整历史', requiresAuth: true },
      },
      {
        path: 'task/:id',
        name: 'ProcessingTaskDetail',
        component: () => import('@/views/processing/TaskDetail.vue'),
        meta: { title: '任务详情', requiresAuth: true },
      },
      {
        path: 'task-edit',
        name: 'ProcessingTaskEdit',
        component: () => import('@/views/processing/TaskEdit.vue'),
        meta: { title: '编辑加工任务', requiresAuth: true },
      },
      {
        path: 'task-management',
        name: 'ProcessingTaskManagement',
        component: () => import('@/views/processing/TaskManagement.vue'),
        meta: { title: '任务调度', requiresAuth: true },
      },
      {
        path: 'application-statistics',
        name: 'ProcessingApplicationStatistics',
        component: () => import('@/views/processing/ApplicationStatistics.vue'),
        meta: { title: '申请统计', requiresAuth: true },
      },
      {
        path: 'equipment-oee',
        name: 'ProcessingEquipmentOEE',
        component: () => import('@/views/processing/EquipmentOEE.vue'),
        meta: { title: '设备OEE分析', requiresAuth: true },
      },
      {
        path: 'person-performance',
        name: 'ProcessingPersonPerformance',
        component: () => import('@/views/processing/PersonPerformance.vue'),
        meta: { title: '人员绩效统计', requiresAuth: true },
      },
      {
        path: 'sub-process',
        name: 'ProcessingSubProcess',
        component: () => import('@/views/processing/SubProcessManagement.vue'),
        meta: { title: '备料工序管理', requiresAuth: true },
      },
      {
        path: 'prep-execution/:recordId?',
        name: 'ProcessingPrepExecution',
        component: () => import('@/views/processing/PrepExecution.vue'),
        meta: { title: '备料工序执行', requiresAuth: true },
      },
      {
        path: 'prep-monitor/:recordId?',
        name: 'ProcessingPrepMonitor',
        component: () => import('@/views/processing/PrepMonitor.vue'),
        meta: { title: '备料工序监控', requiresAuth: true },
        // 同时支持「带记录ID」(/prep-monitor/123) 与「带模号」(/prep-monitor?moldNumber=XXX) 两种进入方式
        props: (route) => ({
          recordId: route.params.recordId as string | undefined,
          moldNumber: (route.query.moldNumber as string) || undefined
        })
      },
      {
        path: 'self-check-execution/:recordId',
        name: 'ProcessingSelfCheckExecution',
        component: () => import('@/views/processing/SelfCheckExecution.vue'),
        meta: { title: '裸模自检执行', requiresAuth: true },
      },
      {
        path: 'guide-slot-edm',
        name: 'ProcessingGuideSlotEdm',
        component: () => import('@/views/processing/GuideSlotEdmExecution.vue'),
        meta: { title: '导料槽EDM', requiresAuth: true },
      },
      {
        path: 'cleaning-drying',
        name: 'ProcessingCleaningDrying',
        component: () => import('@/views/processing/CleaningDrying.vue'),
        meta: { title: '清洗烘干管理', requiresAuth: true },
      },
      {
        path: 'drill-layer',
        name: 'ProcessingDrillLayer',
        component: () => import('@/views/processing/DrillLayer.vue'),
        meta: { title: '进泥孔精加工管理', requiresAuth: true },
      },
      {
        path: 'drill-non-layer-monitor/:configId?',
        name: 'ProcessingDrillNonLayerMonitor',
        component: () => import('@/views/processing/DrillNonLayerMonitor.vue'),
        meta: { title: '进泥孔工序监控', requiresAuth: true },
        // 主入口在任务详情页右栏；此路由仅作深链备用（/drill-non-layer-monitor 或 ?moldNumber=XXX）
        props: (route) => ({
          configId: route.params.configId as string | undefined,
          moldNumber: (route.query.moldNumber as string) || undefined
        })
      },
    ],
  },

  // ========== 模库管理 ==========
  {
    path: '/mold-library',
    redirect: '/mold/list',
  },
  {
    path: '/mold',
    name: 'Mold',
    component: () => import('@/views/mold/MoldLayout.vue'),
    meta: { title: '模库管理', requiresAuth: true },
    redirect: '/mold/list',
    children: [
      {
        path: 'list',
        name: 'MoldList',
        component: () => import('@/views/mold/MoldList.vue'),
        meta: { title: '模具列表', requiresAuth: true },
      },
      {
        path: 'create',
        name: 'MoldCreate',
        component: () => import('@/views/mold/MoldForm.vue'),
        meta: { title: '新增模具', requiresAuth: true },
      },
      {
        path: 'edit/:id',
        name: 'MoldEdit',
        component: () => import('@/views/mold/MoldForm.vue'),
        meta: { title: '编辑模具', requiresAuth: true },
      },
      {
        path: ':id',
        name: 'MoldDetail',
        component: () => import('@/views/mold/MoldDetail.vue'),
        meta: { title: '模具详情', requiresAuth: true, permissions: ['mold:detail:view'] },
      },
      {
        path: 'slot-check',
        name: 'SlotCheck',
        component: () => import('@/views/mold/SlotCheck.vue'),
        meta: { title: '槽宽检测', requiresAuth: true },
      },
      {
        path: 'forming-records',
        name: 'FormingRecordList',
        component: () => import('@/views/mold/FormingRecordList.vue'),
        meta: { title: '成型记录', requiresAuth: true },
      },
      {
        // [2026-10-10 用户要求] 成型记录专用详情页，替代原「查看」弹窗
        path: 'forming-records/:id',
        name: 'FormingRecordDetail',
        component: () => import('@/views/mold/FormingRecordDetail.vue'),
        meta: { title: '成型记录详情', requiresAuth: true },
      },
      {
        path: 'parts',
        name: 'PartList',
        component: () => import('@/views/mold/PartList.vue'),
        meta: { title: '配件列表', requiresAuth: true },
      },
      {
        // [2026-10-12 用户要求] 配件详情页：列表点行进入，基础信息+领用信息+领用/归还记录
        path: 'parts/:id',
        name: 'PartDetail',
        component: () => import('@/views/mold/PartDetail.vue'),
        meta: { title: '配件详情', requiresAuth: true },
      },
      {
        path: 'storage',
        name: 'StorageManagement',
        component: () => import('@/views/mold/StorageManagement.vue'),
        meta: { title: '入库管理', requiresAuth: true },
      },
      {
        path: 'storage-records',
        name: 'StorageRecordList',
        component: () => import('@/views/mold/StorageRecordList.vue'),
        meta: { title: '入库记录', requiresAuth: true },
      },
      {
        path: 'borrow-management',
        name: 'BorrowManagement',
        component: () => import('@/views/mold/PickupManagement.vue'),
        meta: { title: '领还管理', requiresAuth: true, permission: 'mold:borrow' },
      },
      {
        path: 'part-borrow-approval',
        name: 'PartBorrowApproval',
        component: () => import('@/views/mold/PartPickupApproval.vue'),
        meta: { title: '配件领用审批', requiresAuth: true, permission: 'mold:borrow' },
      },
      {
        path: 'my-part-borrow',
        name: 'MyPartBorrow',
        component: () => import('@/views/mold/MyPartPickupRecords.vue'),
        meta: { title: '我的配件领用', requiresAuth: true },
      },
      {
        path: 'return-application',
        name: 'ReturnApplication',
        component: () => import('@/views/mold/ReturnApplicationList.vue'),
        meta: { title: '归还申请', requiresAuth: true },
      },
      {
        path: 'mold-transfer',
        name: 'MoldTransfer',
        component: () => import('@/views/mold/MoldTransferList.vue'),
        meta: { title: '模具交接', requiresAuth: true },
      },
      {
        path: 'compare',
        name: 'MoldCompare',
        component: () => import('@/views/mold/MoldCompare.vue'),
        meta: { title: '模具对比', requiresAuth: true },
      },
      {
        path: 'reprocess',
        name: 'MoldReprocess',
        component: () => import('@/views/mold/MoldReprocessList.vue'),
        meta: { title: '模具再处理', requiresAuth: true },
      },
      {
        path: 'product',
        name: 'MoldProduct',
        component: () => import('@/views/mold/ProductManagement.vue'),
        meta: { title: '成品管理', requiresAuth: true },
      },
      {
        path: 'meters-rank',
        name: 'MoldMetersRank',
        component: () => import('@/views/mold/MoldMetersRank.vue'),
        meta: { title: '米数排行', requiresAuth: true },
      },
    ],
  },

  // ========== 调模管理 ==========
  // {
  //   path: '/tuning',
  //   name: 'Tuning',
  //   component: () => import('@/views/tuning/TuningLayout.vue'),
  //   meta: { title: '调模管理', requiresAuth: true },
  //   redirect: '/tuning/recent-molds',
  //   children: [
  //     {
  //       path: '/tuning/recent-molds',
  //       name: 'TuningRecentMolds',
  //       component: () => import('@/views/tuning/RecentMolds.vue'),
  //       meta: { title: '近期下模具', requiresAuth: true }
  //     },
  //     {
  //       path: '/tuning/task-list',
  //       name: 'TuningTaskList',
  //       component: () => import('@/views/tuning/TaskList.vue'),
  //       meta: { title: '调模任务列表', requiresAuth: true }
  //     },
  //     {
  //       path: '/tuning/records',
  //       name: 'TuningRecords',
  //       component: () => import('@/views/tuning/Records.vue'),
  //       meta: { title: '调模记录', requiresAuth: true }
  //     }
  //   ]
  // },

  // ========== 成型管理 ==========
  // 成型记录页已注册在 /mold 父路由下：mold/forming-records (FormingRecordList)
  {
    path: '/forming',
    name: 'FormingDashboard',
    component: () => import('@/views/forming/FormingDashboard.vue'),
    meta: { title: '成型主页', requiresAuth: true },
  },
  {
    path: '/forming/match',
    name: 'FormingMatchMold',
    component: () => import('@/views/forming/FormingMatchMold.vue'),
    meta: { title: '匹配模具', requiresAuth: true },
  },
  {
    path: '/forming/tasks',
    name: 'FormingTasks',
    component: () => import('@/views/forming/FormingTasks.vue'),
    meta: { title: '成型任务', requiresAuth: true },
  },

  // ========== 设备管理 ==========
  {
    path: '/equipment',
    name: 'Equipment',
    component: () => import('@/views/equipment/EquipmentLayout.vue'),
    meta: { title: '设备管理', requiresAuth: true },
    redirect: '/equipment/list',
    children: [
      {
        path: '/equipment/list',
        name: 'EquipmentList',
        component: () => import('@/views/equipment/EquipmentList.vue'),
        meta: { title: '设备列表', requiresAuth: true },
      },
      {
        path: '/equipment/workshop',
        name: 'EquipmentWorkshop',
        component: () => import('@/views/equipment/Workshop.vue'),
        meta: { title: '车间管理', requiresAuth: true },
      },
      {
        path: '/equipment/baseline-import',
        name: 'EquipmentBaselineImport',
        component: () => import('@/views/equipment/BaselineImport.vue'),
        meta: { title: '保养基准导入', requiresAuth: true },
      },
      {
        path: '/equipment/runtime-import',
        name: 'EquipmentRuntimeImport',
        component: () => import('@/views/equipment/RuntimeImport.vue'),
        meta: { title: '运行时长导入', requiresAuth: true },
      },
      {
        path: '/equipment/annual-plan',
        name: 'EquipmentAnnualPlan',
        component: () => import('@/views/equipment/AnnualPlan.vue'),
        meta: { title: '年度保养总计划', requiresAuth: true },
      },
      {
        path: '/equipment/maintenance',
        name: 'EquipmentMaintenance',
        component: () => import('@/views/equipment/Maintenance.vue'),
        meta: { title: '设备检修', requiresAuth: true },
      },
      {
        path: '/equipment/repair',
        name: 'EquipmentRepair',
        component: () => import('@/views/equipment/Repair.vue'),
        meta: { title: '维修工单', requiresAuth: true },
      },
      {
        path: '/equipment/repair/order',
        name: 'EquipmentRepairOrder',
        component: () => import('@/views/equipment/RepairOrder.vue'),
        meta: { title: '我的报修', requiresAuth: true },
      },
      {
        path: '/equipment/workpoint-summary',
        name: 'EquipmentWorkpointSummary',
        component: () => import('@/views/equipment/WorkpointSummary.vue'),
        meta: { title: '工分统计', requiresAuth: true },
      },
      {
        path: '/equipment/detail',
        name: 'EquipmentDetail',
        component: () => import('@/views/equipment/EquipmentForm.vue'),
        meta: { title: '设备详情', requiresAuth: true },
      },
      {
        path: '/equipment/edit',
        name: 'EquipmentEdit',
        component: () => import('@/views/equipment/EquipmentForm.vue'),
        meta: { title: '编辑设备', requiresAuth: true },
      },
      {
        path: '/equipment/create',
        name: 'EquipmentCreate',
        component: () => import('@/views/equipment/EquipmentForm.vue'),
        meta: { title: '新增设备', requiresAuth: true },
      },
    ],
  },

  // ========== 耗材管理 ==========
  {
    path: '/consumable',
    name: 'Consumable',
    component: () => import('@/views/consumable/ConsumableLayout.vue'),
    meta: { title: '耗材管理', requiresAuth: true },
    redirect: '/consumable/list',
    children: [
      {
        path: '/consumable/list',
        name: 'ConsumableList',
        component: () => import('@/views/consumable/ConsumableList.vue'),
        meta: { title: '耗材列表', requiresAuth: true },
      },
      {
        path: '/consumable/inbound',
        name: 'ConsumableInbound',
        component: () => import('@/views/consumable/Inbound.vue'),
        meta: { title: '耗材入库', requiresAuth: true },
      },
      {
        path: '/consumable/outbound',
        name: 'ConsumableOutbound',
        component: () => import('@/views/consumable/Outbound.vue'),
        meta: { title: '耗材出库', requiresAuth: true },
      },
    ],
  },

  // ========== 绩效管理 ==========
  // {
  //   path: '/performance',
  //   name: 'Performance',
  //   component: () => import('@/views/performance/PerformanceLayout.vue'),
  //   meta: { title: '绩效管理', requiresAuth: true },
  //   redirect: '/performance/statistics',
  //   children: [
  //     {
  //       path: '/performance/statistics',
  //       name: 'PerformanceStatistics',
  //       component: () => import('@/views/performance/Statistics.vue'),
  //       meta: { title: '绩效统计', requiresAuth: true }
  //     },
  //     {
  //       path: '/performance/personal',
  //       name: 'PerformancePersonal',
  //       component: () => import('@/views/performance/Personal.vue'),
  //       meta: { title: '个人绩效', requiresAuth: true }
  //     },
  //     {
  //       path: '/performance/team',
  //       name: 'PerformanceTeam',
  //       component: () => import('@/views/performance/Team.vue'),
  //       meta: { title: '团队绩效', requiresAuth: true }
  //     }
  //   ]
  // },

  // ========== 系统管理 ==========
  {
    path: '/system',
    name: 'System',
    component: () => import('@/views/system/SystemLayout.vue'),
    meta: { title: '系统管理', requiresAuth: true },
    redirect: '/system/user',
    children: [
      {
        path: '/system/user',
        name: 'SystemUser',
        component: () => import('@/views/system/UserManagement.vue'),
        meta: { title: '用户管理', requiresAuth: true },
      },
      {
        path: '/system/role',
        name: 'SystemRole',
        component: () => import('@/views/system/RoleManagement.vue'),
        meta: { title: '角色管理', requiresAuth: true },
      },
      {
        path: '/system/department',
        name: 'SystemDepartment',
        component: () => import('@/views/system/DepartmentManagement.vue'),
        meta: { title: '部门管理', requiresAuth: true },
      },
      {
        path: '/system/permission',
        name: 'SystemPermission',
        component: () => import('@/views/system/PermissionManagement.vue'),
        meta: { title: '权限管理', requiresAuth: true },
      },
      {
        path: '/system/permission-management',
        redirect: '/system/permission',
      },
      {
        path: '/system/permission-log',
        name: 'SystemPermissionLog',
        component: () => import('@/views/system/PermissionLog.vue'),
        meta: { title: '权限日志', requiresAuth: true },
      },
      {
        path: '/system/settings',
        name: 'SystemSettings',
        component: () => import('@/views/system/SystemSettings.vue'),
        meta: { title: '系统设置', requiresAuth: true },
      },
      {
        path: '/system/notification',
        name: 'NotificationCenter',
        component: () => import('@/views/system/NotificationCenter.vue'),
        meta: { title: '消息中心', requiresAuth: true },
      },
      {
        path: '/system/flow-designer',
        name: 'FlowDesigner',
        component: () => import('@/views/system/FlowDesigner.vue'),
        meta: { title: '流程设计器', requiresAuth: true },
      },
      {
        path: '/system/point-standard',
        name: 'PointStandardManagement',
        component: () => import('@/views/system/PointStandardManagement.vue'),
        meta: { title: '标准积分表管理', requiresAuth: true, permission: 'system:point-standard:manage' },
      },
    ],
  },
]

// 创建路由实例 - 根据环境自动选择模式
// 外网环境（如Natapp隧道）使用Hash模式更稳定
const isNatapp =
  window.location.hostname.includes('natappfree') ||
  window.location.hostname.includes('loclx') ||
  (!window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1'))

const router = createRouter({
  history: isNatapp ? createWebHashHistory() : createWebHistory(),
  routes,
})

// 路由守卫 - 权限验证
router.beforeEach(async (to, _from, next) => {
  // Hash 模式 URL 重定向到 History 模式（仅在History模式下执行）
  if (!window.location.hash && window.location.href.includes('#/')) {
    const hashIndex = window.location.href.indexOf('#/')
    if (hashIndex !== -1) {
      const hashPath = window.location.href.substring(hashIndex + 1)
      const pathParts = hashPath.split('?')
      const cleanPath = pathParts[0] || '/'
      const queryString = pathParts[1] || ''

      const redirectData: { path: string; replace: boolean; query?: Record<string, string> } = {
        path: cleanPath,
        replace: true,
      }

      if (queryString) {
        const query: Record<string, string> = {}
        const params = new URLSearchParams(queryString)
        params.forEach((value, key) => {
          query[key] = value
        })
        redirectData.query = query
      }

      next(redirectData)
      return
    }
  }

  const authStore = useAuthStore()
  const requiresAuth = to.meta.requiresAuth !== false

  // 允许访问登录页
  if (to.name === 'Login') {
    if (to.meta.title) {
      document.title = `${to.meta.title} - 安天模具管理系统`
    }
    next()
    return
  }

  // 强制要求登录（即使有token也需要验证有效性）
  if (requiresAuth) {
    if (!authStore.isAuthenticated || !authStore.token) {
      // 未登录，跳转到登录页
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
    // 有token但无用户信息，尝试获取用户信息
    if (!authStore.user) {
      // token存在但无用户信息，重新登录
      authStore.logout()
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    // 已登录但访问登录页，跳转到首页
    next({ name: 'Home' })
    return
  }

  // 权限校验：激活路由 meta 上声明的权限要求（此前仅校验登录，meta.permissions 为死配置）
  // - meta.permissions: string[]（需全部满足）
  // - meta.permission: string（单个）
  // 防御式策略：管理员放行；当用户权限列表为空时 fail-open（避免旧缓存会话被误锁，
  // 后端 API 层仍有 403 兜底），仅在确有权限数据且缺失所需权限时才拦截。
  if (requiresAuth && authStore.user) {
    const required: string[] = []
    if (Array.isArray(to.meta.permissions)) {
      required.push(...(to.meta.permissions as string[]))
    }
    if (typeof to.meta.permission === 'string') {
      required.push(to.meta.permission)
    }

    if (required.length > 0) {
      // 多角色支持：主角色或任一副角色为 ADMIN 均视为管理员
      const isAdmin = authStore.hasRole('ADMIN')
      const userPerms = authStore.currentUser?.permissions || []
      const hasAll = required.every((p) => userPerms.includes(p))
      if (!isAdmin && userPerms.length > 0 && !hasAll) {
        // 有权限数据但缺少所需权限 → 拦截回首页
        next({ name: 'Home' })
        return
      }
    }
  }

  if (to.meta.title) {
    document.title = `${to.meta.title} - 安天模具管理系统`
  }
  next()
})

export default router

// ========== 刷新页面中转路由（多标签页"刷新页面"功能用） ==========
// /redirect/xxx → 立即 replace 回 /xxx，实现组件重建
router.addRoute({
  path: '/redirect/:pathMatch(.*)*',
  redirect: (to) => {
    const pathMatch = to.params.pathMatch
    const target = Array.isArray(pathMatch) ? pathMatch.join('/') : String(pathMatch || '')
    return { path: '/' + target.replace(/^\/+/, ''), query: to.query, replace: true }
  },
})
